import throttle from 'lodash/throttle';

import SyntaxHighlightWorker from './syntax-highlight.worker?worker';

import type {
  SyntaxHighlightLanguage,
  SyntaxHighlightLine,
  SyntaxHighlightTheme,
  SyntaxHighlightWorkerResponse,
} from './syntax-highlight.types';
import type { getSingletonHighlighter as getShikiSingletonHighlighter } from 'shiki';

const MAX_TOKEN_CACHE_SIZE = 500;
const MAX_PENDING_HIGHLIGHT_JOBS = 32;
const ANIMATING_HIGHLIGHT_THROTTLE_MS = 100;
const HIGHLIGHT_JOB_CANCELLED = 'Syntax highlight job cancelled';
const HIGHLIGHT_JOB_SUPERSEDED = 'Syntax highlight job superseded';

// Keep these shared with hover prewarming so cached work matches the render path.
export const MAX_CHARS_TO_EAGER_SYNTAX_HIGHLIGHT = 5_000;
export const MAX_LINES_TO_EAGER_SYNTAX_HIGHLIGHT = 200;
export const VIRTUALIZED_CODE_CHUNK_LINE_COUNT = 40;

export const splitCodeLinesForHighlighting = (text: string) =>
  text.split(/\r\n|\r|\n/);

export const shouldEagerHighlightCode = (
  code: string,
  lineCount = splitCodeLinesForHighlighting(code).length
) =>
  code.length <= MAX_CHARS_TO_EAGER_SYNTAX_HIGHLIGHT &&
  lineCount <= MAX_LINES_TO_EAGER_SYNTAX_HIGHLIGHT;

export const chunkCodeLinesForHighlighting = (lines: string[]) => {
  const chunks: string[] = [];

  for (
    let index = 0;
    index < lines.length;
    index += VIRTUALIZED_CODE_CHUNK_LINE_COUNT
  ) {
    chunks.push(
      lines.slice(index, index + VIRTUALIZED_CODE_CHUNK_LINE_COUNT).join('\n')
    );
  }

  return chunks;
};

interface HighlightRequestOptions {
  code: string;
  language: SyntaxHighlightLanguage;
  priority?: number;
  throttle?: boolean;
  theme: SyntaxHighlightTheme;
}

interface HighlightJob extends HighlightRequestOptions {
  cacheKey: string;
  cancelled: boolean;
  reject: (error: Error) => void;
  resolve: (lines: SyntaxHighlightLine[]) => void;
  sequence: number;
}

interface WorkerCallback {
  reject: (error: Error) => void;
  resolve: (lines: SyntaxHighlightLine[]) => void;
}

export interface HighlightRequestHandle {
  cancel: () => void;
  promise: Promise<SyntaxHighlightLine[]>;
}

const tokenCache = new Map<string, SyntaxHighlightLine[]>();
const queue: HighlightJob[] = [];
const workerCallbacks = new Map<number, WorkerCallback>();

let activeJob: HighlightJob | null = null;
let worker: Worker | null = null;
let workerUnavailable = false;
let workerRequestId = 0;
let jobSequence = 0;
let fallbackHighlighterInstance: Awaited<
  ReturnType<typeof getShikiSingletonHighlighter>
> | null = null;

const getCacheKey = ({ code, language, theme }: HighlightRequestOptions) =>
  `${language ?? 'text'}:${theme}:${code}`;

const manageTokenCacheSize = () => {
  if (tokenCache.size <= MAX_TOKEN_CACHE_SIZE) return;

  const firstKey = tokenCache.keys().next().value;
  if (firstKey) tokenCache.delete(firstKey);
};

const touchCachedTokenLines = (cacheKey: string) => {
  const cachedLines = tokenCache.get(cacheKey);
  if (!cachedLines) return null;

  tokenCache.delete(cacheKey);
  tokenCache.set(cacheKey, cachedLines);
  return cachedLines;
};

export const peekCachedCodeHighlight = (
  options: Pick<HighlightRequestOptions, 'code' | 'language' | 'theme'>
) => tokenCache.get(getCacheKey(options)) ?? null;

const getWorker = () => {
  if (worker || workerUnavailable) return worker;

  try {
    worker = new SyntaxHighlightWorker();
    worker.onmessage = (event: MessageEvent<SyntaxHighlightWorkerResponse>) => {
      const response = event.data;
      const callback = workerCallbacks.get(response.id);
      if (!callback) return;

      workerCallbacks.delete(response.id);

      if (response.ok) {
        callback.resolve(response.lines);
      } else {
        callback.reject(new Error(response.error));
      }
    };
    worker.onerror = (event) => {
      workerUnavailable = true;
      workerCallbacks.forEach(({ reject }) =>
        reject(new Error(event.message || 'Syntax highlight worker failed'))
      );
      workerCallbacks.clear();
      worker?.terminate();
      worker = null;
    };
  } catch {
    workerUnavailable = true;
  }

  return worker;
};

const runHighlightInWorker = (
  job: HighlightJob
): Promise<SyntaxHighlightLine[]> => {
  const activeWorker = getWorker();
  if (!activeWorker) return runHighlightOnMainThread(job);

  const id = ++workerRequestId;

  return new Promise<SyntaxHighlightLine[]>((resolve, reject) => {
    workerCallbacks.set(id, { resolve, reject });
    activeWorker.postMessage({
      id,
      code: job.code,
      language: job.language,
      theme: job.theme,
    });
  }).catch(() => runHighlightOnMainThread(job));
};

const runHighlightOnMainThread = async ({
  code,
  language,
  theme,
}: HighlightRequestOptions): Promise<SyntaxHighlightLine[]> => {
  if (!fallbackHighlighterInstance) {
    const { getSingletonHighlighter } = await import('shiki');

    fallbackHighlighterInstance = await getSingletonHighlighter({
      themes: ['github-light', 'github-dark'],
      langs: [
        'markdown',
        'text',
        'bash',
        'html',
        'css',
        'javascript',
        'typescript',
      ],
    });
  }

  const loadedLanguages = fallbackHighlighterInstance.getLoadedLanguages();
  let lang = language;

  if (language && !loadedLanguages.includes(language)) {
    try {
      await fallbackHighlighterInstance.loadLanguage(language);
    } catch {
      lang = 'text';
    }
  }

  const lines = fallbackHighlighterInstance.codeToTokens(code || ' ', {
    lang,
    theme,
  });

  return (lines?.tokens ?? []).map((line) =>
    line.map((token) => ({
      content: token.content,
      color: token.color,
    }))
  );
};

const trimQueue = () => {
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    if (queue[index]?.cancelled) queue.splice(index, 1);
  }

  while (queue.length > MAX_PENDING_HIGHLIGHT_JOBS) {
    const job = queue.pop();
    job?.reject(new Error(HIGHLIGHT_JOB_SUPERSEDED));
  }
};

const processQueue = () => {
  if (activeJob) return;

  trimQueue();
  const nextJob = queue.shift();
  if (!nextJob) return;

  if (nextJob.cancelled) {
    processQueue();
    return;
  }

  activeJob = nextJob;

  runHighlightInWorker(nextJob)
    .then((lines) => {
      if (!nextJob.cancelled) {
        tokenCache.set(nextJob.cacheKey, lines);
        manageTokenCacheSize();
        nextJob.resolve(lines);
      }
    })
    .catch((error) => {
      nextJob.reject(error instanceof Error ? error : new Error(String(error)));
    })
    .finally(() => {
      activeJob = null;
      scheduleProcessQueue();
    });
};

const throttledProcessQueue = throttle(
  processQueue,
  ANIMATING_HIGHLIGHT_THROTTLE_MS,
  {
    leading: true,
    trailing: true,
  }
);

const scheduleProcessQueue = () => {
  if (activeJob) return;

  trimQueue();
  const nextJob = queue[0];
  if (!nextJob) return;

  if (nextJob.throttle) {
    throttledProcessQueue();
    return;
  }

  processQueue();
};

export const requestCodeHighlight = (
  options: HighlightRequestOptions
): HighlightRequestHandle => {
  const cacheKey = getCacheKey(options);
  const cachedLines = touchCachedTokenLines(cacheKey);

  if (cachedLines) {
    return {
      cancel: () => {},
      promise: Promise.resolve(cachedLines),
    };
  }

  let job: HighlightJob;

  const promise = new Promise<SyntaxHighlightLine[]>((resolve, reject) => {
    job = {
      ...options,
      cacheKey,
      cancelled: false,
      priority: options.priority ?? 0,
      reject,
      resolve,
      sequence: jobSequence++,
    };

    queue.push(job);
    queue.sort(
      (left, right) =>
        (left.priority ?? 0) - (right.priority ?? 0) ||
        left.sequence - right.sequence
    );
    scheduleProcessQueue();
  });

  return {
    cancel: () => {
      if (job.cancelled) return;

      job.cancelled = true;
      const queuedIndex = queue.indexOf(job);
      if (queuedIndex >= 0) queue.splice(queuedIndex, 1);
      job.reject(new Error(HIGHLIGHT_JOB_CANCELLED));
    },
    promise,
  };
};

export const isExpectedSyntaxHighlightError = (error: unknown) =>
  error instanceof Error &&
  (error.message === HIGHLIGHT_JOB_CANCELLED ||
    error.message === HIGHLIGHT_JOB_SUPERSEDED);
